// Acciones de baja de una suscripción, en la ficha del cliente.
//
// Dos acciones sobre la misma ruta (POST /admin/users/{id}/subscription/cancel):
//   - "Dar de baja": baja ordenada. El usuario conserva el plan que pagó hasta
//     el fin del período; al vencer pasa al plan gratuito. Pide Usuarios en
//     nivel completo.
//   - "Cortar ahora": corte inmediato, aunque haya período pago por delante. Es
//     plata y es difícil de revertir, así que además del nivel pide el permiso
//     por cuenta `danger_zone`. El diálogo ADVIERTE que el período ya facturado
//     no se reintegra solo por cortar, y que la cuenta queda en el plan gratuito
//     (no se la deja sin plan: para bloqueo existe otra acción).
//
// El `partner_id` sale de la fila de la suscripción: un usuario puede tener
// subs con varios partners y hay que dar de baja la que el operador está
// mirando, no una adivinada.
//
// El backend revalida siempre el nivel y el permiso; acá solo se esconde lo que
// la sesión no puede usar.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarX } from 'lucide-react';
import {
  adminCancelSubscription,
  AdminApiError,
  type SubscriptionCancelMode,
  type SubscriptionCancelResult,
} from '@/lib/api';
import { absoluteTime, shortDate } from '@/lib/format';
import { useSession } from '@/hooks/useSession';
import { canWrite } from '@/lib/access';
import ConfirmDialog from '@/components/ConfirmDialog';
import { RetryNotice, type RetryNoticeKind } from '@/components/ChargeRetry';
import { fieldCls } from '@/lib/form-styles';

const btn =
  'min-h-[2.25rem] inline-flex items-center gap-2 rounded-lg px-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand-light';

/** Texto del resultado. Un `outcome` nuevo del backend no compila hasta tener su texto. */
function describeCancel(r: SubscriptionCancelResult): { kind: RetryNoticeKind; text: string } {
  const until = r.access_until ? absoluteTime(r.access_until) : null;
  switch (r.outcome) {
    case 'scheduled':
      return {
        kind: 'ok',
        text: `Baja registrada. Conserva el plan${until ? ` hasta el ${until}` : ''} y después pasa al plan gratuito. No se le va a cobrar de nuevo.`,
      };
    case 'already_scheduled':
      return {
        kind: 'info',
        text: `Ya estaba dada de baja: no se volvió a llamar a la pasarela. Conserva el acceso${until ? ` hasta el ${until}` : ''}.`,
      };
    case 'switched_now':
      return {
        kind: 'info',
        text: 'El período pago ya había terminado o estaba en gracia: pasó al plan gratuito ahora.',
      };
    case 'moved_to_free':
      return {
        kind: 'warn',
        text: `Acceso pago cortado ahora${until ? ` (${until})` : ''}. La cuenta quedó en el plan gratuito. El período ya facturado NO se reintegra solo: si hay que devolver plata, usá la herramienta de reintegro de un cobro duplicado.`,
      };
    case 'expired':
      return {
        kind: 'warn',
        text: `Acceso pago cortado ahora${until ? ` (${until})` : ''}. El partner ${r.partner_id} no tiene plan gratuito vigente, así que la suscripción quedó expirada (sin acceso). El período ya facturado NO se reintegra solo: si hay que devolver plata, usá la herramienta de reintegro de un cobro duplicado.`,
      };
  }
}

export default function SubscriptionCancelActions({
  userId,
  partnerId,
  planName,
  status,
  cancelledAt,
  autoRenew,
  currentPeriodEnd,
}: {
  userId: string;
  partnerId: string;
  planName: string | null;
  status: string;
  cancelledAt: string | null;
  autoRenew: boolean;
  currentPeriodEnd: string | null;
}): JSX.Element | null {
  const session = useSession();
  const qc = useQueryClient();
  const canAct = canWrite(session, 'users');
  const hasDangerZone = session?.permissions?.['danger_zone'] === true;

  const [mode, setMode] = useState<SubscriptionCancelMode | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ kind: RetryNoticeKind; text: string } | null>(null);

  const mut = useMutation({
    mutationFn: (m: SubscriptionCancelMode) =>
      adminCancelSubscription(userId, { partnerId, mode: m, reason: reason.trim() }),
    onSuccess: (res) => {
      setMode(null);
      setReason('');
      setNotice(describeCancel(res));
      void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });

  // Solo tiene sentido sobre una sub viva: en pending/expired/cancelled no hay
  // acceso pago que dar de baja.
  const live = status === 'trialing' || status === 'active' || status === 'grace_period';
  if (!live) return null;

  const alreadyCancelled = cancelledAt !== null && !autoRenew;
  const immediate = mode === 'immediate';

  return (
    <div className="mt-3 pt-3 border-t border-surface-border dark:border-surface-dark-border space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!canAct || mut.isPending || alreadyCancelled}
          onClick={() => {
            setNotice(null);
            setMode('end_of_period');
          }}
          title={
            alreadyCancelled
              ? 'Ya está dada de baja: conserva el plan hasta el fin del período.'
              : 'Baja ordenada: conserva el plan hasta el fin del período pago.'
          }
          className={`${btn} bg-status-warning/15 text-status-warning hover:bg-status-warning/25`}
        >
          <CalendarX size={16} className="shrink-0" aria-hidden="true" />
          Dar de baja
        </button>
        {/* El corte inmediato es una acción sobre plata: sin el permiso por
            cuenta no se muestra el botón (el backend igual lo revalida). */}
        {hasDangerZone ? (
          <button
            type="button"
            disabled={!canAct || mut.isPending}
            onClick={() => {
              setNotice(null);
              setMode('immediate');
            }}
            title="Corta el acceso pago ahora, aunque haya período pago por delante: la cuenta queda en el plan gratuito. No reintegra automáticamente."
            className={`${btn} bg-status-error/15 text-status-error hover:bg-status-error/25`}
          >
            <Ban size={16} className="shrink-0" aria-hidden="true" />
            Cortar ahora
          </button>
        ) : (
          <span className="text-xs text-ink-subtle self-center">
            El corte inmediato requiere el permiso «danger_zone».
          </span>
        )}
      </div>

      {notice ? <RetryNotice {...notice} onClose={() => setNotice(null)} /> : null}

      <ConfirmDialog
        open={mode !== null}
        title={immediate ? 'Cortar el acceso ahora' : 'Dar de baja la suscripción'}
        danger={immediate}
        description={
          <div className="space-y-3">
            {immediate ? (
              <>
                <p>
                  Vas a cortar el acceso pago de <strong>{planName ?? partnerId}</strong> ahora
                  mismo
                  {currentPeriodEnd
                    ? `, aunque el período pago llega hasta el ${shortDate(currentPeriodEnd)}`
                    : ''}
                  . La cuenta <strong>queda en el plan gratuito</strong> del partner: no se la deja
                  sin plan (para dejarla sin poder entrar está el bloqueo de cuenta).
                </p>
                <p className="text-status-warning">
                  El período ya facturado <strong>no se reintegra solo</strong> por cortar. Si hay
                  que devolver plata, se hace aparte con la herramienta de reintegro de un cobro
                  duplicado.
                </p>
                <p className="text-ink-subtle text-xs">
                  Si el partner no tiene un plan gratuito vigente, no hay dónde dejar la cuenta y
                  la suscripción queda expirada.
                </p>
              </>
            ) : (
              <p>
                Vas a dar de baja <strong>{planName ?? partnerId}</strong>.{' '}
                {currentPeriodEnd
                  ? `El usuario conserva el plan hasta el ${shortDate(currentPeriodEnd)} y después pasa al plan gratuito.`
                  : 'Al no tener período pago abierto, pasa al plan gratuito ahora.'}{' '}
                No se le va a cobrar de nuevo.
              </p>
            )}
            <label className="block text-sm">
              <span className="font-medium">Motivo (requerido):</span>
              <textarea
                rows={2}
                value={reason}
                maxLength={500}
                required
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej.: el cliente pidió la baja por teléfono"
                className={fieldCls({ extra: 'mt-1' })}
              />
            </label>
            {mut.isError ? (
              <p className="text-status-error text-xs">
                {mut.error instanceof AdminApiError
                  ? mut.error.message
                  : 'No se pudo dar de baja la suscripción.'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={immediate ? 'Cortar ahora' : 'Dar de baja'}
        loading={mut.isPending}
        onConfirm={() => {
          if (mode === null || !reason.trim()) return;
          mut.mutate(mode);
        }}
        onCancel={() => {
          setMode(null);
          setReason('');
          mut.reset();
        }}
      />
    </div>
  );
}
